import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { styled } from '@mui/system';
import { uploadAssetWithDefaultFolder } from '../utils/frontend_assetManager'; // Updated import
import { useAuth } from '../contexts/AuthContext'; // Use AuthContext for permissions and organization
//import './styles/assetManager.css'; // Include CSS for consistent styling

const UploadInput = styled('input')({
  display: 'none',
});

const AssetUploadDialog = ({ open, onClose, onUploadSuccess, assetType }) => {
  const [file, setFile] = useState(null);
  const [tags, setTags] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { currentUser, organizationId, userPermissions, initialized, getToken } = useAuth(); // Include getToken
  const [fileDims, setFileDims] = useState(null);
//console.log("[AssetUploadDialog] Initialized:", initialized);
//console.log("[AssetUploadDialog] Current User:", currentUser);
//console.log("[AssetUploadDialog] Permissions:", userPermissions);

if (!initialized) {
    console.log("[AssetUploadDialog] Waiting for AuthContext to initialize.");
    return null;
}
const handleFileChange = (e) => {
  if (e.target.files && e.target.files[0]) {
    const picked = e.target.files[0];
    setFile(picked);

   /* --- measure naturalWidth / naturalHeight ------------------- */
   if (picked.type.startsWith('image/')) {
     const url  = URL.createObjectURL(picked);
     const img  = new Image();
     img.onload = () => {
       setFileDims({ width: img.width, height: img.height });
       URL.revokeObjectURL(url);
     };
     img.src = url;
   } else {
     setFileDims(null);   // not an image (video, etc.)
   }
  }
};


  const handleUpload = async () => {
    if (file) {
      try {
//        console.log("[AssetUploadDialog] userPermissions:", userPermissions);
        setUploading(true);
        setErrorMessage(''); // Clear previous errors

        const tagsArray = tags.split(',').map((tag) => tag.trim());
        const additionalMetadata = {
          description,
          tags: tagsArray,
          assetType, // Include the asset type
        };

        // Fetch the Firebase ID token using getToken directly
        const token = await getToken();
//        console.log("[AssetUploadDialog] Firebase ID Token:", token);

        if (!token) {
          throw new Error("Firebase ID Token is undefined or null.");
        }

        console.log("[AssetUploadDialog] Calling uploadAssetWithDefaultFolder with params:");
        console.log("File:", file);
        console.log("Organization ID:", organizationId);
        console.log("User ID:", currentUser.uid);
        console.log("Additional Metadata:", additionalMetadata);
        console.log("User Permissions:", userPermissions);
        console.log("Token:", token);

        const uploadedAsset = await uploadAssetWithDefaultFolder(
          file,
          organizationId,
          currentUser.uid,
          additionalMetadata,
          null, // No specific collectionId provided
          userPermissions,
          assetType,
          token
        );

        console.log('Asset uploaded successfully:', uploadedAsset);

        onUploadSuccess(
          {
            ...uploadedAsset,
            assetType: uploadedAsset.assetType || assetType,
            systemAssetType:
              uploadedAsset.systemAssetType ||
              uploadedAsset.assetType ||
              assetType,
            organizationId:
              uploadedAsset.organizationId ||
              organizationId,
          },
          fileDims                    /* ← second argument! */
        );


// Reset state
setFile(null);
setTags('');
setDescription('');
setErrorMessage('');
onClose();
} catch (error) {
  console.error('Error uploading asset:', error);

  // Enhanced error messaging based on backend validation
  const errorMsg = error?.message || '';

  if (errorMsg.includes('Invalid width for icon')) {
    setErrorMessage('Icon is too wide. Max allowed width is 250px.');
  } else if (errorMsg.includes('Invalid height for icon')) {
    setErrorMessage('Icon is too tall. Max allowed height is 150px.');
  } else if (errorMsg.includes('Icon dimensions too large')) {
    setErrorMessage(
      'Icons must be no larger than 250px wide × 150px tall. Please upload a smaller image.'
    );
  } else if (errorMsg.includes('Invalid dimensions for banner')) {
    setErrorMessage(
      'Banners must be between 600–1920px wide and 200–600px tall. Please choose a properly sized image.'
    );
  } else if (errorMsg.includes('Minimum dimensions are')) {
    setErrorMessage(errorMsg); // fallback to backend error message
  } else if (errorMsg.includes('Invalid PNG header')) {
    setErrorMessage('Invalid PNG file format. Please upload a valid image.');
  } else if (errorMsg.includes('Failed to read image metadata')) {
    setErrorMessage(
      'Failed to read image metadata. Make sure your file is a valid image.'
    );
  } else {
    setErrorMessage(
      'Failed to upload asset. Please check your permissions or try again later.'
    );
  }
} finally {
  setUploading(false);
}


    }
  };


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className="asset-upload-dialog"
    >
      <DialogTitle className="modal-title">Upload Asset</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* File Upload Input */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              justifyContent: 'space-between',
            }}
          >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body1">
              {file ? file.name : 'No file selected'}
            </Typography>

            {/* Dimension Hint */}
            <Typography variant="caption" color="textSecondary">
              {assetType === 'icon'
                ? 'Max 250px wide × 150px tall'
                : assetType === 'banner'
                ? 'Min 600×200px | Max 1920×600px'
                : ''}
            </Typography>
              {/* Dimension Error Message */}
              {errorMessage && (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ mt: 1 }}
                >
                  {errorMessage}
                </Typography>
              )}

          </Box>
            <label htmlFor="file-upload">
              <UploadInput
                id="file-upload"
                type="file"
                accept="image/*,video/*"
                onChange={handleFileChange}
              />
              <IconButton
                color="primary"
                component="span"
                aria-label="upload file"
                className="upload-button"
              >
                <UploadFileIcon />
              </IconButton>
            </label>
          </Box>

          {/* Tags Input */}
          <TextField
            fullWidth
            variant="outlined"
            label="Tags (comma-separated)"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            className="text-field"
          />

          {/* Description Input */}
          <TextField
            fullWidth
            multiline
            minRows={3}
            variant="outlined"
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="text-field"
          />
        </Box>
      </DialogContent>

      {/* Dialog Actions */}
      <DialogActions className="modal-actions">
        <Button onClick={onClose} color="secondary" variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          color="primary"
          variant="contained"
          disabled={!file || uploading}
        >
          {uploading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Upload'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AssetUploadDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUploadSuccess: PropTypes.func.isRequired, 
  assetType: PropTypes.string.isRequired, 
};

export default AssetUploadDialog;
